/**
 * Rental Check-In Sheet (reference/Check-In_Sheet_-_NEW.pdf): check-out and check-in side by
 * side, the computed comparison, and the Manager's Billed / Waived decisions on new damage.
 * The inspections and photos themselves are done on the iPad (camera capture).
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CLEANLINESS_LABELS, type Me } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { usedText, type Inspection, type RentalView } from '../../lib/form-types';
import type { WorkOrderDetail } from '../../lib/types';
import { Button, Card, ErrorText, Field, Input, Loading, Modal, Select } from '../../components/ui';
import { SigImage } from './SignaturesTab';

export function RentalTab({ w, me, readOnly, onChange }: { w: WorkOrderDetail; me: Me; readOnly: boolean; onChange: () => void }) {
  const qc = useQueryClient();
  const key = ['wo-rental', w.id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<RentalView>(`/api/work-orders/${w.id}/rental`) });
  const [error, setError] = useState<string | null>(null);
  const [agreement, setAgreement] = useState<string | null>(null);
  const [photo, setPhoto] = useState<{ slot: string; fileId: number } | null>(null);
  if (q.isLoading || !q.data) return <Loading />;
  const v = q.data;
  const isManager = me.role === 'ADMIN' || me.role === 'MANAGER';

  const post = async (url: string, body: object) => {
    setError(null);
    try {
      qc.setQueryData(key, await api.post<RentalView>(url, body));
      onChange();
    } catch (e) {
      setError(errorText(e));
    }
  };

  const rows: { label: string; get: (i: Inspection) => string }[] = [
    { label: 'Machine hours', get: (i) => i.hours ?? '—' },
    { label: 'Fuel / gallons added', get: (i) => `${i.fuelLevel ?? '—'} / ${i.fuelGallonsAdded ?? '—'}` },
    { label: 'DEF / jugs added', get: (i) => `${i.defLevel ?? '—'} / ${i.defJugsAdded ?? '—'}` },
  ];
  const itemNames = (v.out ?? v.in)?.items.map((x) => x.item) ?? [];

  return (
    <div className="space-y-4">
      <ErrorText error={error} />
      <Card title="RENTAL CHECK-IN SHEET">
        <div className="grid gap-3 text-sm md:grid-cols-4">
          <div><span className="text-slate-500">Date</span><div>{date(v.rental?.outAt ?? w.openedAt)}</div></div>
          <div><span className="text-slate-500">Customer</span><div>{w.customer.name}</div></div>
          <div><span className="text-slate-500">Machine model</span><div>{w.machine.make} {w.machine.model}</div></div>
          <div><span className="text-slate-500">Serial number</span><div>{w.machine.serialNumber}</div></div>
          <div><span className="text-slate-500">Controls</span><div>{v.controls.map((c) => (w.machine.controls === c ? <b key={c} className="mr-2">{c}</b> : <span key={c} className="mr-2 text-slate-400">{c}</span>))}</div></div>
          <div><span className="text-slate-500">Check-out</span><div>{v.rental?.checkout ? `#${v.rental.checkout.number}` : '—'}{v.rental?.outAt && ` · ${dateTime(v.rental.outAt)}`}</div></div>
          <div><span className="text-slate-500">Check-in</span><div>{v.rental?.checkin ? `#${v.rental.checkin.number}` : '—'}{v.rental?.inAt && ` · ${dateTime(v.rental.inAt)}`}</div></div>
          <div className="flex items-end gap-2">
            <Field label="Agreement no."><Input value={agreement ?? v.rental?.agreementNo ?? ''} onChange={(e) => setAgreement(e.target.value)} disabled={readOnly} /></Field>
            {!readOnly && agreement !== null && <Button variant="secondary" onClick={() => post(`/api/work-orders/${w.id}/rental`, { agreementNo: agreement }).then(() => setAgreement(null))}>Save</Button>}
          </div>
        </div>
        {v.phase === 'IN' && v.openRentals && !readOnly && (
          <div className="mt-3 flex items-end gap-2 text-sm">
            <Field label="This machine came back from check-out…">
              <Select value={v.rental?.id ?? ''} onChange={(e) => e.target.value && post(`/api/work-orders/${w.id}/rental`, { rentalId: Number(e.target.value) })}>
                <option value="">Choose…</option>
                {v.openRentals.map((r) => <option key={r.id} value={r.id}>#{r.checkoutNumber} {r.outAt ? `(out ${date(r.outAt)})` : '(not checked out yet)'}</option>)}
              </Select>
            </Field>
          </div>
        )}
      </Card>

      <Card title="Check out / Check in">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="w-1/3" /><th>Check out</th><th>Check in</th></tr></thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.label}><td className="py-1 font-medium">{r.label}</td><td>{v.out ? r.get(v.out) : '—'}</td><td>{v.in ? r.get(v.in) : '—'}</td></tr>
              ))}
              {itemNames.map((name) => {
                const o = v.out?.items.find((x) => x.item === name);
                const n = v.in?.items.find((x) => x.item === name);
                const changed = o?.status === 'OK' && n?.status === 'ISSUE';
                return (
                  <tr key={name} className={changed ? 'bg-red-50' : ''}>
                    <td className="py-1 font-medium">{name}</td>
                    <td><Status s={o?.status ?? null} c={o?.comment ?? null} /></td>
                    <td><Status s={n?.status ?? null} c={n?.comment ?? null} />{changed && <span className="ml-1 rounded bg-red-600 px-1 text-xs text-white">New damage / condition change</span>}</td>
                  </tr>
                );
              })}
              <tr><td className="py-1 font-medium">Cleanliness</td><td>{v.out?.cleanliness ? CLEANLINESS_LABELS[v.out.cleanliness] : '—'}</td><td>{v.in?.cleanliness ? CLEANLINESS_LABELS[v.in.cleanliness] : '—'}</td></tr>
              <tr><td className="py-1 font-medium">Operator's manual</td><td>{yn(v.out?.operatorsManual)}</td><td>{yn(v.in?.operatorsManual)}</td></tr>
              <tr><td className="py-1 font-medium">Attachments</td><td>{v.out?.attachments.join(', ') || '—'}</td><td>{v.in?.attachments.join(', ') || '—'}</td></tr>
              <tr><td className="py-1 font-medium">Initials</td><td>{v.out?.initials ?? '—'}</td><td>{v.in?.initials ?? '—'}</td></tr>
              <tr><td className="py-1 font-medium">Notes</td><td className="whitespace-pre-wrap">{v.out?.notes ?? '—'}</td><td className="whitespace-pre-wrap">{v.in?.notes ?? '—'}</td></tr>
              <tr><td className="py-1 font-medium">Submitted</td><td>{v.out?.submittedAt ? dateTime(v.out.submittedAt) : 'not yet'}</td><td>{v.in?.submittedAt ? dateTime(v.in.submittedAt) : 'not yet'}</td></tr>
            </tbody>
          </table>
        </div>
        {[v.out, v.in].map((i) => i && i.missing.length > 0 && <p key={i.phase} className="mt-2 rounded bg-amber-50 p-2 text-sm text-amber-900">{i.phase === 'OUT' ? 'Check-out' : 'Check-in'} still needs: {i.missing.join(', ')}</p>)}
      </Card>

      {v.comparison && (
        <Card title="At check-in">
          <dl className="grid gap-3 text-sm md:grid-cols-3">
            <div><dt className="text-slate-500">Hours used</dt><dd className="text-lg font-semibold">{v.comparison.hoursUsed ?? '—'}</dd></div>
            <div><dt className="text-slate-500">Fuel</dt><dd className="text-lg font-semibold">{usedText(v.comparison.fuelUsed)}</dd></div>
            <div><dt className="text-slate-500">DEF</dt><dd className="text-lg font-semibold">{usedText(v.comparison.defUsed)}</dd></div>
          </dl>
          <h3 className="mt-4 font-semibold">New damage / condition change</h3>
          {v.comparison.newDamage.length === 0 && <p className="text-sm text-slate-500">None.</p>}
          <ul className="mt-1 space-y-2">
            {v.comparison.newDamage.map((d) => (
              <li key={d.item} className="flex flex-wrap items-center gap-2 rounded border border-red-200 p-2 text-sm">
                <span className="font-semibold">{d.item}</span>
                <span className="text-slate-600">{d.outStatus ? `${d.outStatus} → ` : ''}{d.inStatus === 'ADDITIONAL_TIME' ? CLEANLINESS_LABELS.ADDITIONAL_TIME : d.inStatus}{d.comment && ` — ${d.comment}`}</span>
                <span className="ml-auto flex items-center gap-2">
                  {d.billing ? <b className={d.billing === 'BILLED' ? 'text-green-800' : 'text-slate-700'}>{d.billing === 'BILLED' ? 'Billed' : 'Waived'}</b> : <span className="text-red-700">Not decided</span>}
                  {isManager && !readOnly && (
                    <>
                      <Button variant="secondary" onClick={() => post(`/api/work-orders/${w.id}/rental/damage`, { item: d.item, billing: 'BILLED' })}>Billed</Button>
                      <Button variant="secondary" onClick={() => post(`/api/work-orders/${w.id}/rental/damage`, { item: d.item, billing: 'WAIVED' })}>Waived</Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {!isManager && v.comparison.newDamage.some((d) => !d.billing) && <p className="mt-2 text-xs text-slate-500">The Manager marks each Billed or Waived before the job can close.</p>}
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {[v.out, v.in].map((i, idx) => (
          <Card key={idx} title={`Photos — ${idx === 0 ? 'check out' : 'check in'}`}>
            {!i && <p className="text-sm text-slate-500">Not started.</p>}
            <div className="grid grid-cols-3 gap-2">
              {i?.photos.map((p) => (
                <button key={p.slot} disabled={!p.fileId} onClick={() => p.fileId && setPhoto({ slot: p.slot, fileId: p.fileId })} className="overflow-hidden rounded border text-left">
                  {p.fileId ? <img src={`/api/files/${p.fileId}`} alt={p.slot} loading="lazy" className="aspect-[4/3] w-full object-cover" /> : <div className="flex aspect-[4/3] items-center justify-center bg-slate-100 text-xs text-red-700">missing</div>}
                  <div className="p-1 text-xs">{p.slot}</div>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>
      <Card title="Signatures">
        <div className="grid gap-4 md:grid-cols-2">
          <SigImage label="Customer signature" sig={v.signatures.customer} />
          <SigImage label="Technician signature" sig={v.signatures.technician} />
        </div>
      </Card>
      {photo && (
        <Modal title={photo.slot} onClose={() => setPhoto(null)}>
          <img src={`/api/files/${photo.fileId}`} alt={photo.slot} className="w-full" />
        </Modal>
      )}
    </div>
  );
}

const yn = (b: boolean | null | undefined) => (b === true ? 'Yes' : b === false ? 'No' : '—');

function Status({ s, c }: { s: 'OK' | 'ISSUE' | null; c: string | null }) {
  if (!s) return <span className="text-slate-400">—</span>;
  return (
    <span>
      <b className={s === 'OK' ? 'text-green-800' : 'text-red-700'}>{s}</b>
      {c && <span className="text-slate-600"> — {c}</span>}
    </span>
  );
}
